import { rethrowIfDisposable } from '@design-system/utils/disposableEmailError'

/**
 * Provides comprehensive login and authentication management functionality.
 *
 * This composable handles various login-related operations including:
 * - Email validation
 * - Login state management
 * - Authentication checks
 * - Registration handling
 * - Logout functionality
 *
 * @returns {Object} An object containing login-related methods and states
 * @property {Ref<string>} loginEmail - Current login email address
 * @property {Ref<boolean>} isLogged - Indicates whether the user is currently logged in
 * @property {Function} loginCheckEmail - Checks email existence and verification status
 * @property {Function} login - Performs user login
 * @property {Function} logout - Logs out the current user
 *
 * @example
 * const {
 *   loginEmail,
 *   isLogged,
 *   loginCheckEmail,
 *   login,
 *   logout
 * } = useLogin()
 */
export const useLogin = () => {
  //REGISTRATION METHODS
  const { registrationUser, cleanRegistrationData } = useRegistration()
  const { emailExists, emailExistsCheckout, login, loginError } = useAuth0()
  const { dispatchAccountLogoutEvent } = useGAAccountEvents()

  const { resetCart } = useCart()
  const { loggedIn, clear } = useUserSession()
  const isFederatedApp = computed(() => useRuntimeConfig().public.isFederated)
  const isLoggedFederated = useState('isLoggedFederated', () => false)
  const isClient = import.meta.client
  const isLogged = computed(() => {
    if (isFederatedApp.value) {
      return isLoggedFederated.value
    }
    return loggedIn.value && isClient ? loggedIn.value : false
  })

  // CONST
  const LOGIN_FORM_ID = 'login-form-id'
  const LOGIN_FORM_ID_SIGNUP_PAGE = 'login-form-id-signup-page'
  const LOGIN_EMAIL_STATE = 'LOGIN_EMAIL_STATE'
  const LOGIN_EMAIL_VALID_STATE = 'LOGIN_EMAIL_VALID_STATE'
  const LOGIN_PSW_STATE = 'LOGIN_PSW_STATE'
  const EMAIL_EXIST_0N_AUTH0_STATE = 'EMAIL_EXIST_0N_AUTH0_STATE'
  const EMAIL_EXIST_0N_AUTH0_SALESFORCE = 'EMAIL_EXIST_0N_AUTH0_SALESFORCE'
  const EMAIL_VERIFIED_STATE = 'EMAIL_VERIFIED_STATE'
  const IS_REGISTRATION_STATE = 'IS_REGISTRATION_STATE'
  const LOGIN_SOCIAL_ACCOUNT_CONNECTION = 'LOGIN_SOCIAL_ACCOUNT_CONNECTION'

  const loginEmail = useState<string>(LOGIN_EMAIL_STATE, () => '')
  const loginEmailIsValid = useState<boolean>(
    LOGIN_EMAIL_VALID_STATE,
    () => false
  )
  const loginPassword = useState<string>(LOGIN_PSW_STATE, () => '')
  const emailExistOnAuth0 = useState<boolean>(
    EMAIL_EXIST_0N_AUTH0_STATE,
    () => false
  )
  const emailExistOnSF = useState<boolean>(
    EMAIL_EXIST_0N_AUTH0_SALESFORCE,
    () => false
  )
  const emailVerified = useState<boolean>(EMAIL_VERIFIED_STATE, () => false)
  const isRegistration = useState<boolean>(IS_REGISTRATION_STATE, () => false)
  const socialLoginConnection = useState<string>(
    LOGIN_SOCIAL_ACCOUNT_CONNECTION,
    () => ''
  )

  /**
   * Checks email existence and verification status for login.
   *
   * @param {string} email - The email to check
   * @param {boolean} [showLoader=true] - Whether to show a loading indicator
   * @returns {Promise<boolean>} Whether the email exists and is verified
   */
  const loginCheckEmail = async (email: string, showLoader = true) => {
    loginCleanStatus()
    const { checkTokenReCAPTCHA } = useRecaptchaSite()

    try {
      await checkTokenReCAPTCHA('loginCheckEmail')
      // Move to useAuth
      const { userOrFail, sfExists, verified, socialAccountConnection } =
        await emailExists(email, showLoader)

      handleSFExists(sfExists)
      handleUserOrFail(userOrFail, verified, email, socialAccountConnection)

      return emailExistOnAuth0.value
    } catch (error) {
      // let the caller show the disposable-email message under the input
      rethrowIfDisposable(error)
      // Handle any errors
      console.error(error)
      // You can add additional error handling logic here
      return false
    }
  }

  /**
   * Checks email existence and verification status during checkout.
   *
   * @param {string} email - The email to check
   * @param {boolean} [showLoader=true] - Whether to show a loading indicator
   * @returns {Promise<boolean>} Whether the email exists and is verified
   */
  const loginCheckEmailCheckout = async (email: string, showLoader = true) => {
    loginCleanStatus()
    const { checkTokenReCAPTCHA } = useRecaptchaSite()

    try {
      await checkTokenReCAPTCHA('loginCheckEmail')
      // Move to useAuth
      const { userOrFail, sfExists, verified, socialAccountConnection } =
        await emailExistsCheckout(email, showLoader)

      handleSFExists(sfExists)
      handleUserOrFail(userOrFail, verified, email, socialAccountConnection)

      return emailExistOnAuth0.value
    } catch (error) {
      console.error(error)
      return false
    }
  }

  /**
   * Handles Salesforce email existence.
   *
   * @param {boolean} sfExists - Whether the email exists in Salesforce
   * @private
   */
  const handleSFExists = (sfExists: boolean) => {
    if (sfExists) {
      emailExistOnSF.value = true
    }
  }

  /**
   * Handles user verification status.
   *
   * @param {boolean} userOrFail - User existence status
   * @param {boolean} verified - Email verification status
   * @param {string} email - User email
   * @param {string} socialConnection - Social login connection type
   * @private
   */
  const handleUserOrFail = (
    userOrFail: boolean,
    verified: boolean,
    email: string,
    socialConnection: string
  ) => {
    if (userOrFail && verified) {
      handleUserVerified(socialConnection)
    } else {
      handleUserNotVerified(email)
    }
  }

  /**
   * Handles verified user login process.
   *
   * @param {string} socialConnection - Social login connection type
   * @private
   */
  const handleUserVerified = (socialConnection: string) => {
    emailExistOnAuth0.value = true
    emailVerified.value = true
    isRegistration.value = true
    socialLoginConnection.value = socialConnection

    if (isRegistration.value) {
      isRegistration.value = false
      cleanRegistrationData()
    }
  }

  /**
   * Handles unverified user login process.
   *
   * @param {string} email - User email
   * @private
   */
  const handleUserNotVerified = (email: string) => {
    emailExistOnAuth0.value = false
    emailVerified.value = false
    isRegistration.value = true
    registrationUser.value.email = email
  }

  /**
   * Sets the login email address.
   *
   * @param {string} newEmail - New email address to set
   */
  const setLoginEmail = (newEmail: string) => (loginEmail.value = newEmail)

  /**
   * Resets login-related state variables.
   * @private
   */
  const loginCleanStatus = () => {
    socialLoginConnection.value = ''
    emailExistOnAuth0.value = false
    emailExistOnSF.value = false
    emailVerified.value = false
    loginEmailIsValid.value = false
    isRegistration.value = false
  }

  /**
   * Clears login credentials and related states.
   */
  const loginCleanCredentials = () => {
    loginEmail.value = ''
    loginPassword.value = ''
    loginCleanStatus()
  }

  /**
   * Logs out the current user.
   *
   * @param {Object} [options] - Logout options
   * @param {boolean} [options.noReload=false] - Whether to skip page reload after logout
   */
  const logout = async ({ noReload = false }: { noReload?: boolean } = {}) => {
    resetCart()
    await clear()
    if (noReload) return
    window.location.reload()
    dispatchAccountLogoutEvent()
  }

  return {
    LOGIN_FORM_ID,
    LOGIN_FORM_ID_SIGNUP_PAGE,
    loginEmail,
    emailExistOnSF,
    loginEmailIsValid,
    isRegistration,
    emailExistOnAuth0,
    emailVerified,
    loginPassword,
    isLogged,
    loginError,
    socialLoginConnection,
    isLoggedFederated,
    loginCheckEmail,
    loginCheckEmailCheckout,
    loginCleanCredentials,
    setLoginEmail,
    login,
    logout,
  }
}
